import { ClerkProvider as BaseClerkProvider } from '@clerk/clerk-react';
import { enGB } from '@clerk/localizations';
import { ReactNode } from 'react';
import { PageURL } from '../../router/pageURL';
import { config } from '../../utils/config';
import logo from 'images/StrideUp_Logo.png';
import { ClerkErrorHandler } from './ClerkErrorHandler';

const PUBLISHABLE_KEY = config.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Add your Clerk Publishable Key to the .env file');
}

type ClerkProviderProps = {
  children: ReactNode;
};

function toHref(to: string | URL): string {
  return typeof to === 'string' ? to : to.toString();
}

/** Same-origin redirect only: normalizes href, rejects cross-origin and invalid URLs (e.g. javascript:), falls back to APPLICATIONS. */
function safeRedirect(href: string, replace: boolean): void {
  try {
    const nextUrl = new URL(href, window.location.origin);

    if (nextUrl.origin !== window.location.origin) {
      const fallback = `${window.location.origin}${PageURL.APPLICATIONS}`;
      replace ? window.location.replace(fallback) : (window.location.href = fallback);
      return;
    }

    const target = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

    replace ? window.location.replace(target) : (window.location.href = target);
  } catch {
    const fallback = `${window.location.origin}${PageURL.APPLICATIONS}`;
    replace ? window.location.replace(fallback) : (window.location.href = fallback);
  }
}

export const ClerkProvider = ({ children }: ClerkProviderProps) => {
  return (
    <BaseClerkProvider
      routerPush={(to: string | URL) => safeRedirect(toHref(to), false)}
      routerReplace={(to: string | URL) => safeRedirect(toHref(to), true)}
      publishableKey={PUBLISHABLE_KEY}
      signInUrl={`${window.location.origin}${PageURL.LOGIN}`}
      signUpUrl={`${window.location.origin}${PageURL.SIGNUP}`}
      signInFallbackRedirectUrl={`${window.location.origin}${PageURL.APPLICATIONS}`}
      signUpFallbackRedirectUrl={`${window.location.origin}${PageURL.SIGNUP}`}
      afterSignOutUrl={`${window.location.origin}${PageURL.LOGIN}`}
      localization={enGB}
      appearance={{
        variables: {
          fontSize: '14px',
          colorPrimary: '#004615',
          fontFamilyButtons: 'Satoshi, sans-serif',
        },
        layout: {
          logoPlacement: 'inside',
          logoImageUrl: logo,
        },
        elements: {
          logoBox: {
            height: '5rem',
          },
          button: {
            borderRadius: '0.250rem',
            textTransform: 'uppercase',
          },
          buttonArrowIcon: {
            display: 'none',
          },
        },
      }}
    >
      <ClerkErrorHandler />
      {children}
    </BaseClerkProvider>
  );
};
